/**
 * @fileoverview 配信グループ関連のAPIサービス
 *
 * このファイルは配信グループの作成、更新、削除、ターゲット設定などの
 * API操作を提供するサービスモジュールです。
 *
 * @module DistributionGroupService
 * @requires @vueuse/integrations/useAxios
 * @requires @/clinic/constants/api.router.constants
 * @requires @/common/utils/http-common-utils
 * @requires @vueuse/core
 */

/**
 * VueUseのAxios統合機能を提供するモジュール
 * HTTPリクエストの実行とレスポンス管理を行います
 */
import { useAxios } from "@vueuse/integrations/useAxios";

/**
 * APIルーティング定数を定義するモジュール
 * 配信グループ関連のエンドポイントURLを管理します
 */
import ApiRouterConstants from "@/clinic/constants/api.router.constants";

/**
 * HTTP通信の共通ユーティリティ
 * 認証ヘッダーやエラーハンドリングなどの共通設定を提供します
 */
import http from "@/common/utils/http-common-utils";

/**
 * VueUseのコア機能
 * コールバック関数の型定義を提供します
 */
import { AnyFn, Fn } from "@vueuse/core";

/**
 * 配信グループ関連のAPIサービス
 *
 * 配信グループの作成、更新、削除、ターゲット設定などの操作を提供します。
 */
const DistributionGroupService = {
  /**
   * 配信グループの基本情報を作成
   * @param {any} params - クエリパラメータ
   * @param {any} data - リクエストボディ
   * @returns {Promise<any>} 作成結果
   */
  createBasicInformation: async (params: any, data: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/basic_settings`,
      { method: "POST", params: params, data: data },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 配信グループの基本情報を更新
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @param {any} data - リクエストボディ
   * @returns {Promise<any>} 更新結果
   */
  updateBasicInformation: async (distributionGroupId: any, params: any, data: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/basic_settings`,
      { method: "PATCH", params: params, data: data },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 配信設定を更新
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @param {any} data - リクエストボディ
   * @returns {Promise<any>} 更新結果
   */
  updateDeliverySettings: async (distributionGroupId: any, params: any, data: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/delivery_settings`,
      { method: "PATCH", params: params, data: data },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * すべてのタグを取得
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} タグ一覧
   */
  getAllTags: async (params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/target_settings/get_tags`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 配信グループを削除
   * @param {any} distributionGroupId - 配信グループID
   * @returns {Promise<any>} 削除結果
   */
  destroy: async (distributionGroupId: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}`,
      { method: "DELETE" },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 配信グループ一覧を取得
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} 配信グループ一覧
   */
  getDistributionGroups: async (params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 特定の配信グループを取得
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} 配信グループ詳細
   */
  getDistributionGroup: async (distributionGroupId: any, params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}}`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * フィルター結果を取得
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} フィルター結果
   */
  getFilterResult: async (params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/target_settings/filter`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * ターゲット顧客を保存
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} 保存結果
   */
  saveTargetCustomers: async (distributionGroupId: any, params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/customers`,
      { method: "PATCH", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * ターゲット設定を更新
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @param {any} data - リクエストボディ
   * @returns {Promise<any>} 更新結果
   */
  updateTargetSettings: async (distributionGroupId: any, params: any, data: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/target_settings`,
      { method: "PATCH", params: params, data: data },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * ターゲット顧客を取得
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} ターゲット顧客一覧
   */
  getTargetCustomers: async (distributionGroupId: any, params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/customers`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * ターゲット顧客を削除
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @param {Fn} onSuccess - 成功時のコールバック
   * @param {AnyFn} onError - エラー時のコールバック
   * @returns {Promise<any>} 削除結果
   */
  deleteTargetCustomers: async (distributionGroupId: any, params: any, onSuccess: Fn, onError: AnyFn) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/customers`,
      { method: "DELETE", params: params },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    return await execute();
  },

  /**
   * 複数の配信グループを一括削除
   * @param {any} params - 削除対象のID配列
   * @param {Fn} onSuccess - 成功時のコールバック
   * @param {AnyFn} onError - エラー時のコールバック
   * @returns {Promise<any>} 削除結果
   */
  destroyDistributionGroups: async (params: any, onSuccess: Fn, onError: AnyFn) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/batch_destroy`,
      { method: "DELETE", params: { ids: params } },
      http,
      {
        immediate: false,
        onSuccess,
        onError,
      }
    );
    return await execute();
  },

  /**
   * 配信グループのステータスを変更
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @param {any} data - リクエストボディ
   * @returns {Promise<any>} 変更結果
   */
  changeStatus: async (distributionGroupId: any, params: any, data: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/change_status`,
      { method: "PATCH", params: params, data: data },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 配信グループをブロードキャスト
   * @param {any} distributionGroupId - 配信グループID
   * @param {any} params - クエリパラメータ
   * @returns {Promise<any>} ブロードキャスト結果
   */
  broadcast: async (distributionGroupId: any, params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_DISTRIBUTION_GROUPS}/${distributionGroupId}/broadcast`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },

  /**
   * 顧客ステータスオプションを取得
   *
   * クリニックのパターンに応じた顧客ステータスオプションを取得します。
   * テーブルにデータがない場合は、デフォルトの設定を使用します。
   *
   * @param {Object} params - クエリパラメータ
   * @param {number} params.clinic_id - クリニックID
   * @returns {Promise<Object>} 顧客ステータスオプション一覧
   * @returns {Object} returns.data - レスポンスデータ
   * @returns {Array<Object>} returns.data.customer_statuses - 顧客ステータスオプション配列
   * @returns {string} returns.data.customer_statuses[].value - ステータス値
   * @returns {string} returns.data.customer_statuses[].name - ステータス表示名
   */
  getCustomerStatuses: async (params: any) => {
    const { execute } = useAxios(
      `${ApiRouterConstants.API_CLINICS}/customer_statuses`,
      { method: "GET", params: params },
      http,
      {
        immediate: false,
      }
    );
    return await execute();
  },
};

export default DistributionGroupService;
